import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Divider, type SxProps, type Theme } from "@mui/material";
import { useCallback, useRef } from "react";

import { WheelColumn } from "./WheelColumn";

const HOUR_ITEMS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((h) => ({
  value: h,
  label: String(h),
  ariaLabel: `${h} hours`,
}));

const MINUTE_ITEMS = Array.from({ length: 60 }, (_, index) => ({
  value: index,
  label: String(index).padStart(2, "0"),
  ariaLabel: `${index} minutes`,
}));

const MERIDIEM_ITEMS = ["AM", "PM"].map((m) => ({
  value: m,
  label: m,
  ariaLabel: m,
}));

export interface WheelTimePickerProps {
  value?: Date;
  onChange: (newTime: Date) => void;
  onScrollingChange?: (isScrolling: boolean) => void;
  preventParentTouchStart?: boolean;
  containerSx?: SxProps<Theme>;
  dayIndicator?: string;
}

function getMeridiem(date: Date): "AM" | "PM" {
  return date.getHours() >= 12 ? "PM" : "AM";
}

function getHourIn12HourFormat(date: Date): number {
  return date.getHours() % 12 || 12;
}

function getHourIn24HourFormat(hour12: number, meridiem: "AM" | "PM"): number {
  if (meridiem === "AM" && hour12 === 12) {
    return 0;
  }

  if (meridiem === "PM" && hour12 !== 12) {
    return hour12 + 12;
  }

  return hour12;
}

function buildDate(
  baseValue: Date | undefined,
  hour12: number,
  minute: number,
  meridiem: "AM" | "PM"
): Date {
  const base = isDefined(baseValue) ? new Date(baseValue) : new Date();
  base.setHours(getHourIn24HourFormat(hour12, meridiem), minute, 0, 0);
  return base;
}

export function WheelTimePicker(props: WheelTimePickerProps) {
  const { value, onChange, onScrollingChange, preventParentTouchStart, containerSx, dayIndicator } =
    props;

  const scrollingCountRef = useRef(0);
  const onScrollingChangeRef = useRef(onScrollingChange);
  onScrollingChangeRef.current = onScrollingChange;

  const handleColumnScrollingChange = useCallback((isScrolling: boolean): void => {
    scrollingCountRef.current += isScrolling ? 1 : -1;
    onScrollingChangeRef.current?.(scrollingCountRef.current > 0);
  }, []);

  const now = value ?? new Date();
  const selectedHour = getHourIn12HourFormat(now);
  const selectedMinute = now.getMinutes();
  const selectedMeridiem = getMeridiem(now);

  const handleHourSelect = (selectedHourValue: number | string): void => {
    onChange(buildDate(value, Number(selectedHourValue), selectedMinute, selectedMeridiem));
  };

  const handleMinuteSelect = (selectedMinuteValue: number | string): void => {
    onChange(buildDate(value, selectedHour, Number(selectedMinuteValue), selectedMeridiem));
  };

  const handleMeridiemSelect = (selectedMeridiemValue: number | string): void => {
    onChange(buildDate(value, selectedHour, selectedMinute, selectedMeridiemValue as "AM" | "PM"));
  };

  return (
    <Box
      sx={containerSx}
      onTouchStartCapture={
        preventParentTouchStart
          ? (event) => {
              event.stopPropagation();
            }
          : undefined
      }
    >
      <Box
        sx={{
          display: "flex",
          width: "fit-content",
          margin: "0 auto",
          position: "relative",
        }}
      >
        <WheelColumn
          loop
          name="hours"
          items={HOUR_ITEMS}
          selectedValue={selectedHour}
          onSelect={handleHourSelect}
          onScrollingChange={handleColumnScrollingChange}
        />
        <Divider flexItem orientation="vertical" />
        <WheelColumn
          loop
          name="minutes"
          items={MINUTE_ITEMS}
          selectedValue={selectedMinute}
          onSelect={handleMinuteSelect}
          onScrollingChange={handleColumnScrollingChange}
        />
        <Divider flexItem orientation="vertical" />
        <WheelColumn
          name="meridiem"
          items={MERIDIEM_ITEMS}
          selectedValue={selectedMeridiem}
          onSelect={handleMeridiemSelect}
        />
        {isDefined(dayIndicator) && (
          <Text
            sx={(theme) => ({
              position: "absolute",
              left: "100%",
              top: "50%",
              transform: "translateY(-50%)",
              paddingLeft: 1,
              color: theme.palette.text.tertiary,
              fontSize: "0.875rem",
              whiteSpace: "nowrap",
            })}
          >
            {dayIndicator}
          </Text>
        )}
      </Box>
    </Box>
  );
}
